import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { Stethoscope, VideoCamera, Translate, GraduationCap, Heart } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#4A6750', '#D48C70', '#3B6953', '#5C6660'];

export default function DoctorDirectory() {
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSpecialties();
    fetchDoctors('');
  }, []);

  const fetchSpecialties = async () => {
    const { data } = await axios.get(`${API}/specialties`);
    setSpecialties(data.specialties || []);
  };

  const fetchDoctors = async (specialty) => {
    setLoading(true);
    try {
      const url = specialty ? `${API}/doctors?specialty=${encodeURIComponent(specialty)}` : `${API}/doctors`;
      const { data } = await axios.get(url);
      setDoctors(data.doctors || []);
    } finally {
      setLoading(false);
    }
  };

  const handleSpecialtyChange = (s) => {
    setSelectedSpecialty(s);
    fetchDoctors(s);
  };

  const getInitials = (name) => name.split(' ').filter(p => !p.toLowerCase().startsWith('dr')).map(p => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="doctor-directory-page">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">Our Specialist Panel</p>
            <h1 className="font-spectral text-3xl sm:text-4xl font-bold text-primary mb-3">Book a Video Consultation</h1>
            <p className="text-secondary max-w-2xl mx-auto">
              Browse verified Indian specialists. Pick your preferred doctor by specialty, language, or experience and book a live video consultation.
            </p>
            <p className="text-xs text-secondary mt-2" data-testid="listing-order-note">
              Listings are ordered by specialty and name. No doctor can pay for a higher position.
            </p>
          </div>

          {/* Specialty Filter */}
          <div className="card mb-6">
            <p className="text-sm font-medium text-secondary mb-3">Filter by Specialty:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleSpecialtyChange('')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedSpecialty === '' ? 'bg-primary text-white' : 'bg-surface-secondary text-secondary hover:bg-primary/10'
                }`}
                data-testid="filter-all"
              >
                All Specialties
              </button>
              {specialties.map((s, idx) => (
                <button
                  key={s}
                  onClick={() => handleSpecialtyChange(s)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedSpecialty === s ? 'bg-primary text-white' : 'bg-surface-secondary text-secondary hover:bg-primary/10'
                  }`}
                  data-testid={`filter-specialty-${idx}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Doctor Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : doctors.length === 0 ? (
            <div className="card text-center py-12">
              <Stethoscope size={48} weight="duotone" className="text-secondary mx-auto mb-4 opacity-50" />
              <p className="text-secondary mb-2">No doctors found in this specialty yet.</p>
              <p className="text-xs text-secondary">
                We are actively adding doctors. <a href="https://wa.me/918840804798" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">WhatsApp us</a> if you need urgent help.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctors.map((d, idx) => (
                <div key={d.id} className="card flex flex-col" data-testid={`doctor-card-${idx}`}>
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="flex items-center justify-center w-14 h-14 rounded-full font-bold text-white text-lg flex-shrink-0"
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    >
                      {getInitials(d.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-manrope font-bold text-primary truncate">{d.name}</p>
                      <p className="text-sm text-accent font-medium">{d.specialty}</p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4 text-sm flex-1">
                    {d.credentials && (
                      <div className="flex items-start gap-2">
                        <GraduationCap size={16} weight="duotone" className="text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-secondary">{d.credentials}</span>
                      </div>
                    )}
                    {d.years_experience && (
                      <div className="flex items-start gap-2">
                        <Heart size={16} weight="duotone" className="text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-secondary">{d.years_experience}+ years experience</span>
                      </div>
                    )}
                    {d.languages && (
                      <div className="flex items-start gap-2">
                        <Translate size={16} weight="duotone" className="text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-secondary text-xs">{d.languages}</span>
                      </div>
                    )}
                    {d.bio && <p className="text-xs text-secondary italic line-clamp-2 mt-2">"{d.bio}"</p>}
                  </div>

                  <div className="border-t border-primary/10 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-2xl font-bold text-primary font-manrope">₹{d.consultation_fee || 700}</p>
                        <p className="text-xs text-secondary">per 15 min</p>
                      </div>
                      <VideoCamera size={28} weight="duotone" className="text-accent" />
                    </div>
                    <Link
                      to={`/doctors/${d.id}/book`}
                      className="block w-full text-center py-2.5 btn-primary text-sm"
                      data-testid={`book-doctor-${idx}`}
                    >
                      Book Video Consult
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
